import { ValueStoreFacade, EventServiceFacade } from '../../../JWeb';
import {
  DataCollectionEventNames,
  ReservedValueStoreKeys
} from '../../../JWeb/JWebEnums';
import { PublishCdmEventsOptions } from '../../types';
import IStoreService from '../../../storeService/IStoreService';
import { jWebStates } from '../../../JWeb/Initializer';
import { EventServiceValueType } from '../../../eventService/types';
import { IMonitoringService } from '../../../monitoringService';
import getFlatMap from '../../../../utils/getFlatMap';

/**
 * Checks if all required JWeb plugins are available.
 * Returns an object with the result and updates the enabled state.
 */
export async function checkJWebPluginsAvailability(
  isEnabled: boolean
): Promise<{ isAvailable: boolean; shouldDisable: boolean }> {
  if (!isEnabled) {
    return { isAvailable: false, shouldDisable: false };
  }

  try {
    // Is JWeb ready?
    await jWebStates.JWebLibrary;
    // Are the plugins registered?
    await jWebStates.defaultPluginsRegistered;

    // Are required plugins installed?
    await jWebStates.plugins.ValueStore;
    await jWebStates.plugins.EventService;
    await jWebStates.plugins.DataCollection;

    return { isAvailable: true, shouldDisable: false };
  } catch (error) {
    console.error(
      'Unable to run Analytics. JWeb plugin initialization failed:',
      error
    );
    return { isAvailable: false, shouldDisable: true };
  }
}

/**
 * Writes analytics data to ValueStore for both native and web analytics services.
 */
export async function writeAnalyticsDataToValueStore(
  version: string,
  store: IStoreService,
  options?: PublishCdmEventsOptions
): Promise<void> {
  const hostname = window.location.hostname;

  await ValueStoreFacade.writeDataToValueStore([
    {
      key: ReservedValueStoreKeys.webApplicationName,
      value: hostname
    },
    {
      key: ReservedValueStoreKeys.webApplicationVersion,
      value: version
    },
    {
      key: ReservedValueStoreKeys.selectedPrinterUuid,
      value:
        options?.metadata?.associatedDeviceUuid ||
        store.state.onboarding?.sessionContext?.device?.uuid
    },
    {
      key: ReservedValueStoreKeys.selectedPrinterModelNumber,
      value:
        options?.metadata?.associatedDeviceProductNumber ||
        store.state.onboarding?.sessionContext?.device?.productNumber
    },
    {
      key: ReservedValueStoreKeys.ucdeCorrelationId,
      value:
        options?.metadata?.xCorrelationId ||
        store.state.onboarding?.sessionContext?.xCorrelationId
    }
  ]);
}

/**
 * Handles publish notification errors by logging them to the monitoring service.
 */
export function handlePublishNotificationError(
  event: EventServiceValueType,
  monitoringService: IMonitoringService
): void {
  const { eventData } = event || {};
  if (!eventData) return;

  const isPublished = eventData.action === 'publishNotification';
  const is200Code =
    eventData.actionDetail?.telemetryServiceResponse?.responseCode == 200;

  if (isPublished && !is200Code) {
    // We found an error, so will write it on our monitoring service.
    const dataToBeSaved = Object.fromEntries(
      getFlatMap(eventData.actionDetail)
    );

    monitoringService.startSpan('jshell-analytics', dataToBeSaved).end();
  }
}

/**
 * Subscribes to CDM event status for error monitoring.
 */
export async function subscribeForCmdEventsStratus(
  callback: (event: EventServiceValueType) => void
): Promise<void> {
  const subscriber = await EventServiceFacade.getSubscribe();
  subscriber.subscribe(
    { eventName: DataCollectionEventNames.cdmEventStatus },
    callback
  );
}
